// SchedulerDialog.jsx — simplified & user‑friendly
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem'; // Import MenuItem for the select component
/**
 * A single, easy‑to‑use scheduler dialog for regular pump timings and
 * one‑shot (single‑day) windows. Time‑zone is fixed to IST on the back‑end,
 * so the field has been removed for clarity.
 */

const WEEKDAYS = [
  { label: 'Sun', val: 0 },
  { label: 'Mon', val: 1 },
  { label: 'Tue', val: 2 },
  { label: 'Wed', val: 3 },
  { label: 'Thu', val: 4 },
  { label: 'Fri', val: 5 },
  { label: 'Sat', val: 6 },
];

const blankRecurring = {
  enabled: true,
  dayMask: [1, 2, 3, 4, 5, 6, 0],
  onTime: '06:00',
  offTime: '06:20',
  retryCount: 0,        // Default: no retries
  retryDuration: 10     // Default: 10 minutes between checks
};

export default function SchedulerDialog({ open, onClose, deviceCode }) {
  const [schedule, setSchedule] = useState({ recurring: [blankRecurring], oneShots: [] });
  const [saving, setSaving] = useState(false);

  /* ── load existing schedule ─────────────────────────── */
  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API_BASE_URL}api/motor-schedule/${deviceCode}`)
      .then(res => {
        if (res.data.schedule) {
          const transformedSchedule = {
            recurring: res.data.schedule.recurring.map(r => ({
              enabled: r.enabled,
              dayMask: r.dayMask,
              onTime: formatTimeFromAPI(r.onTime),
              offTime: formatTimeFromAPI(r.offTime),
              retryCount: r.retryCount !== undefined ? r.retryCount : 0,
              retryDuration: r.retryDuration !== undefined ? r.retryDuration : 10
            })),
            oneShots: res.data.schedule.oneShots.map(shot => ({
              ...shot,
              retryCount: shot.retryCount !== undefined ? shot.retryCount : 0,
              retryDuration: shot.retryDuration !== undefined ? shot.retryDuration : 10
            }))
          };
          setSchedule(transformedSchedule);
        } else {
          setSchedule({ recurring: [blankRecurring], oneShots: [] });
        }
      })
      .catch(() => setSchedule({ recurring: [blankRecurring], oneShots: [] }));
  }, [open, deviceCode]);

  /* ── helper mutators ─────────────────────────────────── */
  const updateRecurring = (idx, key, val) =>
    setSchedule(p => {
      const arr = [...p.recurring];
      arr[idx] = { ...arr[idx], [key]: val };
      return { ...p, recurring: arr };
    });

  const formatTimeFromAPI = (timeObj) => {
    return `${String(timeObj.hours).padStart(2, '0')}:${String(timeObj.minutes).padStart(2, '0')}`;
  };

  const parseTimeForAPI = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return { hours, minutes };
  };

  const addRecurring = () => setSchedule(p => ({ ...p, recurring: [...p.recurring, blankRecurring] }));
  const deleteRecurring = idx => setSchedule(p => ({ ...p, recurring: p.recurring.filter((_, i) => i !== idx) }));

  const addOneShot = () =>
    setSchedule(p => ({
      ...p,
      oneShots: [
        ...p.oneShots,
        { 
          onAt: dayjs().add(10, 'm').second(0).toISOString(), 
          offAt: dayjs().add(40, 'm').second(0).toISOString(),
          retryCount: 0,
          retryDuration: 10
        },
      ],
    }));

  const updateOneShot = (idx, field, val) =>
    setSchedule(p => {
      const arr = [...p.oneShots];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...p, oneShots: arr };
    });

  const deleteOneShot = idx => setSchedule(p => ({ ...p, oneShots: p.oneShots.filter((_, i) => i !== idx) }));

  /* ── save handler ────────────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    try {
      const transformedSchedule = {
        timezone: 'Asia/Kolkata',
        recurring: schedule.recurring.map(r => ({
          enabled: r.enabled,
          dayMask: r.dayMask,
          onTime: parseTimeForAPI(r.onTime),
          offTime: parseTimeForAPI(r.offTime),
          retryCount: r.retryCount === '' ? 0 : parseInt(r.retryCount) || 0,
          retryDuration: r.retryDuration === '' ? 0 : parseInt(r.retryDuration) || 0
        })),
        oneShots: schedule.oneShots.map(shot => ({
          ...shot,
          retryCount: shot.retryCount === '' ? 0 : parseInt(shot.retryCount) || 0,
          retryDuration: shot.retryDuration === '' ? 0 : parseInt(shot.retryDuration) || 0
        }))
      };

      await axios.put(`${API_BASE_URL}api/motor-schedule/${deviceCode}`, transformedSchedule);
      onClose(true);
    } catch (err) {
      alert('Could not save schedule.');
    } finally {
      setSaving(false);
    }
  };

  /* ── render ───────────────────────────────────────────── */
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>Motor Scheduler</DialogTitle>

      <DialogContent dividers>
        {/* ================= Recurring slots ================= */}
        <Grid container alignItems="center" sx={{ mb: 1 }}>
          <Grid item flexGrow={1}>
            <strong>Daily / Weekly Slots</strong>
          </Grid>
          <Grid item>
            <Tooltip title="Add slot"><IconButton size="small" onClick={addRecurring}><AddIcon /></IconButton></Tooltip>
          </Grid>
        </Grid>

        {schedule.recurring.map((r, idx) => (
          <React.Fragment key={idx}>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={3} md={2}>
                <FormControlLabel
                  control={<Checkbox checked={r.enabled} onChange={e => updateRecurring(idx, 'enabled', e.target.checked)} />}
                  label="Enable"
                />
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  select
                  label="Days"
                  SelectProps={{ multiple: true, renderValue: sel => sel.map(v => WEEKDAYS.find(w => w.val === v)?.label).join(', ') }}
                  value={r.dayMask}
                  onChange={e => updateRecurring(idx, 'dayMask', e.target.value)}
                  size="small"
                  fullWidth
                >
                  {WEEKDAYS.map(w => <MenuItem key={w.val} value={w.val}>{w.label}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  type="time" label="Start" size="small" fullWidth
                  value={r.onTime}
                  onChange={e => updateRecurring(idx, 'onTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  type="time" label="Stop" size="small" fullWidth
                  value={r.offTime}
                  onChange={e => updateRecurring(idx, 'offTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={1} textAlign="right">
                <Tooltip title="Delete slot"><IconButton onClick={() => deleteRecurring(idx)} size="small"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Grid>
            </Grid>

            {/* Retry settings row */}
            <Grid container spacing={1} alignItems="center" sx={{ mb: 2, ml: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Motor Retry Settings:
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Retry Count"
                  type="number"
                  size="small"
                  value={r.retryCount}
                  onChange={e => updateRecurring(idx, 'retryCount', e.target.value === '' ? '' : parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 0, max: 10 } }}
                  helperText="0-10 retries"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Minutes Between"
                  type="number"
                  size="small"
                  value={r.retryDuration}
                  onChange={e => updateRecurring(idx, 'retryDuration', e.target.value === '' ? '' : parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 0, max: 60 } }}
                  helperText="0-60 mins"
                  fullWidth
                />
              </Grid>
            </Grid>
          </React.Fragment>
        ))}

        {/* ================= One‑shot windows ================= */}
        <Grid container alignItems="center" sx={{ mt: 3, mb: 1 }}>
          <Grid item flexGrow={1}>
            <strong>Single‑Day Window</strong>
          </Grid>
          <Grid item>
            <Tooltip title="Add one‑shot"><IconButton size="small" onClick={addOneShot}><AddIcon /></IconButton></Tooltip>
          </Grid>
        </Grid>

        {schedule.oneShots.map((w, idx) => (
          <React.Fragment key={idx}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid item xs={12} sm={5}>
                  <DateTimePicker
                    label="Start"
                    value={dayjs(w.onAt)}
                    onChange={val => updateOneShot(idx, 'onAt', val.toISOString())}
                    renderInput={props => <TextField fullWidth size="small" {...props} />}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <DateTimePicker
                    label="Stop"
                    value={dayjs(w.offAt)}
                    onChange={val => updateOneShot(idx, 'offAt', val.toISOString())}
                    renderInput={props => <TextField fullWidth size="small" {...props} />}
                  />
                </Grid>
              </LocalizationProvider>
              <Grid item xs={12} sm={1} textAlign="center">
                <Tooltip title="Delete window"><IconButton size="small" onClick={() => deleteOneShot(idx)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Grid>
            </Grid>

            {/* Retry settings row */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2, ml: 1 }}>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Motor Retry Settings:
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Retry Count"
                  type="number"
                  size="small"
                  value={w.retryCount}
                  onChange={e => updateOneShot(idx, 'retryCount', e.target.value === '' ? '' : parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 0, max: 10 } }}
                  helperText="0-10 retries"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Minutes Between"
                  type="number"
                  size="small"
                  value={w.retryDuration}
                  onChange={e => updateOneShot(idx, 'retryDuration', e.target.value === '' ? '' : parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 0, max: 60 } }}
                  helperText="0-60 mins"
                  fullWidth
                />
              </Grid>
            </Grid>
          </React.Fragment>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}
